import { useAuth } from './useAuth';
import { OrgRole } from '@core/types';

/** §9.6 / §10.3 — derived from org role (Keycloak realm roles + Supabase org row). */
export interface Permissions {
  // Candidate
  canViewAllCandidates: boolean;
  canCreateCandidate: boolean;
  canEditCandidate: boolean;
  /** Viewers can adjust lightweight object metadata (e.g. Parameters tab: name, description); full edits need canEditCandidate. */
  canEditCandidateParameters: boolean;
  canDeleteCandidate: boolean;

  // Applications
  canViewAllApplications: boolean;
  canCreateApplication: boolean;
  canUpdateApplicationStatus: boolean;
  canAssignApplication: boolean;

  // Email
  canSendEmail: boolean;
  canViewEmailActivity: boolean;

  // Clients & Jobs
  canManageClients: boolean;
  canManageJobs: boolean;

  // Reports
  canViewReports: boolean;
  canViewIndividualPerformance: boolean;
  canExportReports: boolean;

  // Team
  canViewTeam: boolean;
  canManageTeam: boolean;
  canInviteMembers: boolean;
  canChangeRoles: boolean;

  // Settings & Billing
  canManageMaskingTemplates: boolean;
  canViewBilling: boolean;
  canPurchaseCredits: boolean;

  // Role checks
  isOwner: boolean;
  isAdmin: boolean;
  isSeniorRecruiter: boolean;
  isJuniorRecruiter: boolean;
  isViewer: boolean;
  hasRole: (role: OrgRole) => boolean;
}

const ROLE_PRIORITY: Record<OrgRole, number> = {
  owner: 5,
  admin: 4,
  senior_recruiter: 3,
  junior_recruiter: 2,
  viewer: 1,
};

/**
 * §9.6 — Open another user’s activity from pipeline list: self always; peers require senior+ pipeline visibility or admin performance views.
 */
export function canViewAssigneeActivity(
  perms: Pick<Permissions, 'canViewAllApplications' | 'canViewIndividualPerformance'>,
  viewerOrgUserId: string | null | undefined,
  assigneeOrgUserId: string | null | undefined
): boolean {
  if (!assigneeOrgUserId) return false;
  if (viewerOrgUserId && viewerOrgUserId === assigneeOrgUserId) return true;
  return perms.canViewAllApplications || perms.canViewIndividualPerformance;
}

export function usePermissions(): Permissions {
  const { user } = useAuth();
  const roles = user?.roles ?? [];

  const hasRole = (role: OrgRole) => roles.includes(role);

  const highestPriority = Math.max(
    0,
    ...roles.map((r) => ROLE_PRIORITY[r] ?? 0)
  );

  const atLeast = (role: OrgRole) => highestPriority >= ROLE_PRIORITY[role];

  return {
    // Candidate
    canViewAllCandidates: atLeast('senior_recruiter'),
    canCreateCandidate: atLeast('junior_recruiter'),
    canEditCandidate: atLeast('junior_recruiter'),
    canEditCandidateParameters: atLeast('viewer'),
    canDeleteCandidate: atLeast('admin'),

    // Applications
    canViewAllApplications: atLeast('senior_recruiter'),
    canCreateApplication: atLeast('junior_recruiter'),
    canUpdateApplicationStatus: atLeast('junior_recruiter'),
    canAssignApplication: atLeast('senior_recruiter'),

    // Email
    canSendEmail: atLeast('senior_recruiter'),
    canViewEmailActivity: atLeast('senior_recruiter'),

    // Clients & Jobs
    canManageClients: atLeast('senior_recruiter'),
    canManageJobs: atLeast('senior_recruiter'),

    // Reports
    canViewReports: atLeast('senior_recruiter'),
    canViewIndividualPerformance: atLeast('admin'),
    canExportReports: atLeast('senior_recruiter'),

    // Team
    canViewTeam: atLeast('junior_recruiter'),
    canManageTeam: atLeast('admin'),
    canInviteMembers: atLeast('admin'),
    canChangeRoles: atLeast('admin'),

    // Settings & Billing
    canManageMaskingTemplates: atLeast('senior_recruiter'),
    canViewBilling: atLeast('owner'),
    canPurchaseCredits: atLeast('owner'),

    // Role checks
    isOwner: hasRole('owner'),
    isAdmin: hasRole('admin'),
    isSeniorRecruiter: hasRole('senior_recruiter'),
    isJuniorRecruiter: hasRole('junior_recruiter'),
    isViewer: hasRole('viewer'),
    hasRole,
  };
}
